// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { newGuid } from '@microsoft/applicationinsights-core-js';
import { AzureLogger } from '@azure/logger';

const appInsightsLogAlowList: Array<string | string[]> = [
  'communication-react:callAdapter:error',
  'communication-react:chatAdapter:error',
  'communication-react:component:error',
  'communication-react:callAdapter:verbose',
  'communication-react:chatAdapter:verbose',
  'communication-react:calling-stateful',
  'communication-react:chat-stateful',
  ['azure:ACS:warning', 'CallClient'],
  ['azure:ACS:error', 'CallClient'],
  'azure:communication-chat:verbose',
  'azure:communication-chat:info',
  'azure:communication-chat:warning',
  'azure:communication-chat:error',
  'MaxListenersExceededWarning',
  'ResizeObserver'
];

/**
 * @public
 */
export interface AzureCommunicationLogConnector {
  log: (...args) => void;
  filter: (...args) => boolean;
  appInsights: ApplicationInsights;
}

/**
 * @public
 */
export const createAppInsightsConnector = (
  connectionString: string,
  filter: (...args) => boolean = appInsightsLogFilter
): AzureCommunicationLogConnector => {
  // Initialize and start collecting telemetry
  // A mock AppInsights is created if no AppInsights connection string is provided
  const appInsights = connectionString
    ? new ApplicationInsights({
        config: {
          disableCookiesUsage: true,
          connectionString,
          enableAutoRouteTracking: true
        }
      })
    : new ApplicationInsights({
        config: {
          instrumentationKey: newGuid()
        }
      });

  appInsights.loadAppInsights();
  appInsights.trackPageView();

  const log = (message: string, ...args): void => {
    appInsights?.trackTrace({ message }, ...args);
  };

  return {
    log,
    filter,
    appInsights
  };
};

/**
 * @public
 */
export const appInsightsLogFilter = (message: string): boolean => {
  for (const condition of appInsightsLogAlowList) {
    const matches = typeof condition === 'string' ? [condition] : condition;
    if (matches.length === 0) {
      return false;
    }
    let result = true;
    for (const match of matches) {
      if (!message.includes(match)) {
        result = false;
      }
    }
    if (result) return true;
  }
  return false;
};

/**
 * @public
 */
export const connectAzureCommunicationLog = (connector: AzureCommunicationLogConnector): void => {
  const target = AzureLogger.log;
  AzureLogger.log = (message: string, ...args) => {
    if (connector.filter(message)) {
      connector.log(message, ...args);
    }
    target(message, ...args);
  };
};
